<template>
  <section class="rounded-sm bg-[#232b33] p-4 roundedn-sm">
 
    <header class="mb-3 flex flex-col items-start justify-between gap-2">
      
      <div class="flex items-center justify-between gap-2">
        <h3 class="text-sm font-semibold text-white/90">
          Incident · {{ group.anomalies.length }} {{ group.anomalies.length === 1 ? 'anomaly' : 'anomalies' }}
        </h3>
        <div
          class="rounded px-2 py-0.5 mb-0.5 text-xxs uppercase tracking-wide w-fit"
          :class="severityBadgeClass(group.severity)"
        >
          {{ severityLabel(group.severity) }}
        </div>
      </div>
      
      <!-- On desktop the type badges and the A/B legend sit on one line;
           they stack on mobile. -->
      <div class="flex w-full flex-col gap-1.5 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-4">
        <div class="flex flex-wrap items-center gap-2 text-xs text-white/55">
          <span
            v-if="incidentGroupMeta.id || incidentGroupMeta.name"
            class="inline-flex items-center gap-1 rounded bg-white/10 px-1.5 py-0.5 text-xs font-semibold text-white/75"
            :title="incidentGroupMeta.indicates"
          >
            <span v-if="incidentGroupMeta.id" class="font-bold">{{ incidentGroupMeta.id }}</span>{{ incidentGroupMeta.name }}
          </span>
          <bly-signal-group-legend v-if="incidentGroupMeta.id || incidentGroupMeta.name" align="left" />
          <span
            v-for="meta in resourceTypeBadges"
            :key="meta.label"
            class="inline-flex items-center gap-1 text-xs font-semibold"
            :class="meta.colorClass"
          >
            <i :class="meta.icon"></i>{{ meta.label }}
          </span>
        </div>

        <!-- A/B legend: which resource each letter/colour refers to. -->
        <div v-if="multiResource" class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/55">
          <span
            v-for="r in resourceLegend"
            :key="`leg-${r.key}`"
            class="inline-flex items-center gap-1"
            :title="legendTooltip(r)"
          >
            <span
              class="inline-flex h-4 min-w-[16px] items-center justify-center rounded px-1 text-[10px] font-bold leading-none"
              :class="r.badge"
            >{{ r.letter }}</span>
            <span class="max-w-[180px] truncate" :class="r.text">{{ r.name }}</span>
          </span>
        </div>
      </div>
    </header>

    <div class="mb-2 flex items-center justify-between text-xs text-white/55">
      <span>{{ formatTime(group.startMs) }} -> {{ formatTime(group.endMs) }}</span>
      <button
        class="h-7 px-3 text-xs font-bold tracking-tight rounded cursor-pointer transition-colors bg-yellow-600 text-black hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
        :disabled="analyzing || allAnalyzed"
        @click="$emit('analyze-incidents', { incidentIds: groupIncidentIds })"
      >
        <i v-if="analyzing" class="el-icon-loading mr-1"></i>
        {{ allAnalyzed ? 'All analyzed' : 'Analyze group' }}
      </button>
    </div>

    <!-- Sub-timeline of anomalies within the incident window — one labelled row
         per metric, all of that metric's occurrences on the same row. -->
    <div class="w-full space-y-1 rounded-sm border border-white/10 bg-black/45 p-1">
      <div
        v-for="lane in lanes"
        :key="lane.id"
        class="flex items-center gap-2"
      >
        <div
          class="w-28 shrink-0 truncate text-xs text-white/60 sm:w-40"
          :title="lane.metric"
        >{{ lane.metric || '—' }}</div>
        <div class="relative h-6 flex-1 overflow-hidden rounded-sm">
          <button
            v-for="(placement, itemIndex) in lane.items"
            :key="`${placement.anomaly.id}-${itemIndex}`"
            class="absolute top-0 h-full rounded-sm border-0 p-0 transition-opacity hover:opacity-90"
            :class="[severityZClass(placement.anomaly.severity), anomalyClass(placement.anomaly)]"
            :style="{ left: `${placement.left}%`, width: `${placement.width}%` }"
            :title="anomalyTooltip(placement.anomaly)"
            @click="selectAnomaly(placement.anomaly)"
          ></button>
        </div>
      </div>
    </div>

    <div class="mt-1 flex items-center gap-2">
      <div class="w-28 shrink-0 sm:w-40"></div>
      <div class="relative h-4 flex-1">
        <span class="absolute left-0 text-[10px] text-white/45">{{ formatTimeSeconds(timeWindow.start) }}</span>
        <span class="absolute right-0 text-[10px] text-white/45">{{ formatTimeSeconds(timeWindow.end) }}</span>
      </div>
    </div>

    <!-- On desktop the anomaly list and the selected anomaly detail sit
         side by side; they stack on mobile. -->
    <div class="mt-3 flex flex-col gap-3 lg:flex-row lg:items-start">
      <!-- Anomaly list -->
      <div class="max-h-40 space-y-1 overflow-y-auto pr-1 lg:max-h-80 lg:w-72 lg:shrink-0">
        <button
          v-for="(anomaly, rowIndex) in listAnomalies"
          :key="`row-${anomaly.id}-${rowIndex}`"
          class="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1 text-left text-xs transition-colors"
          :class="selectedAnomalyId === anomaly.id ? 'bg-black/60' : 'bg-black/30 hover:bg-black/50'"
          :title="resourceTooltip(anomaly)"
          @click="selectAnomaly(anomaly)"
        >
          <span class="flex min-w-0 items-center gap-2">
            <span class="h-2 w-2 shrink-0 rounded-full" :class="severityDotClass(anomaly.severity)"></span>
            <i :class="resourceMeta(anomaly.resourceType).icon" class="shrink-0"></i>
            <span class="truncate font-medium" :class="resourceMeta(anomaly.resourceType).colorClass">{{ anomaly.metricName || '-' }}</span>
            <span
              v-if="resourceLabelFor(anomaly)"
              class="shrink-0 inline-flex h-4 min-w-[16px] items-center justify-center rounded px-1 text-[10px] font-bold leading-none"
              :class="resourceLabelFor(anomaly).badge"
            >{{ resourceLabelFor(anomaly).letter }}</span>
            <span
              v-if="anomaly.groupId"
              class="shrink-0 rounded bg-white/10 px-1 text-[10px] font-bold text-white/55"
              :title="signalGroupTooltip(anomaly.groupId, anomaly.groupName)"
            >{{ anomaly.groupId }}</span>
          </span>
          <span class="flex shrink-0 items-center gap-2 text-white/50">
            <span>{{ formatTimeSeconds(anomaly.startMs) }}</span>
            <i v-if="anomaly.hasAi" class="el-icon-cpu text-emerald-300" title="AI analysed"></i>
          </span>
        </button>
      </div>

      <!-- Selected anomaly detail -->
      <div v-if="selectedAnomaly" class="min-w-0 lg:flex-1">
        <bly-event-analytics-details-panel
          :incident="selectedAnomaly"
          :event="event"
          :fallback-range="fallbackRange"
          :analyzing="analyzing"
          :resource-label="resourceLabelFor(selectedAnomaly)"
          :show-header="false"
          @analyze="$emit('analyze-incidents', { incidentIds: 'all' })"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import moment from 'moment';
// `Bly` prefix keeps the kebab-case template tag resolving under script-setup
// automatic component registration.
import BlyEventAnalyticsDetailsPanel from './EventAnalyticsDetailsPanel.vue';
import BlySignalGroupLegend from './SignalGroupLegend.vue';
import { resourceTypeMeta } from '../utils/eventResourceType';
import {
  createTimelineScale,
  severityBg,
  severityZClass,
  severityRank,
  severityLabel,
  severityBadgeClass,
  severityDotClass,
} from '../utils/timelineScale';
import { signalGroupMeta, signalGroupTooltip } from '../utils/signalGroups';

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
  event: {
    type: Object,
    default: null,
  },
  fallbackRange: {
    type: Object,
    default: () => ({ from: null, to: null }),
  },
  analyzing: {
    type: Boolean,
    default: false,
  },
  // When set, this anomaly is pre-selected instead of the first one — used
  // when the user clicks a specific resource issue in the timeline above.
  initialAnomalyId: {
    type: String,
    default: null,
  },
});

defineEmits(['analyze-incidents', 'close']);

const selectedAnomalyId = ref(null);

const sortedAnomalies = computed(() => [...(props.group.anomalies || [])].sort((a, b) => a.startMs - b.startMs));
// Severity-first ordering for the anomaly list and the auto-selection: errors on
// top, then warnings, info last — a short error must not get lost in a wall of
// info rows.
const listAnomalies = computed(() => [...sortedAnomalies.value].sort(
  (a, b) => severityRank(b.severity) - severityRank(a.severity) || a.startMs - b.startMs,
));
// Was `window` in the Options API version; renamed to avoid shadowing the global.
const timeWindow = computed(() => {
  const starts = sortedAnomalies.value.map((a) => a.startMs);
  const ends = sortedAnomalies.value.map((a) => a.endMs);
  let start = Math.min(props.group.startMs, ...(starts.length ? starts : [props.group.startMs]));
  let end = Math.max(props.group.endMs, ...(ends.length ? ends : [props.group.endMs]));
  if (end <= start) end = start + 1000;
  // Add a small padding so instantaneous anomalies remain visible.
  const pad = Math.max((end - start) * 0.04, 1000);
  start -= pad;
  end += pad;
  return { start, end, dur: end - start };
});
// Time -> percent scale for the padded incident window.
const scale = computed(() => createTimelineScale({ start: timeWindow.value.start, end: timeWindow.value.end, minWidth: 1.5 }));
// One lane per metric: every occurrence of the same metric stays on a single
// labelled row, instead of greedy-packing different metrics onto shared rows.
const lanes = computed(() => {
  const byMetric = new Map();
  sortedAnomalies.value.forEach((anomaly) => {
    const metric = anomaly.metricName || '—';
    if (!byMetric.has(metric)) byMetric.set(metric, { id: byMetric.size, metric, items: [], rank: 0 });
    const { left, width, right } = scale.value.bar(anomaly.startMs, anomaly.endMs);
    const lane = byMetric.get(metric);
    lane.items.push({ anomaly, left, width, right });
    lane.rank = Math.max(lane.rank, severityRank(anomaly.severity));
  });
  // Most severe metric lane first (error > warning > info).
  const rows = Array.from(byMetric.values()).sort((a, b) => b.rank - a.rank);
  return rows.length ? rows : [{ id: 0, metric: '', items: [] }];
});
const selectedAnomaly = computed(() => {
  if (!selectedAnomalyId.value) return null;
  return sortedAnomalies.value.find((a) => a.id === selectedAnomalyId.value) || null;
});
const resourceTypeBadges = computed(() => (props.group.resourceTypes || []).map((type) => resourceTypeMeta(type)));
const incidentGroupMeta = computed(() => signalGroupMeta(props.group.groupId, props.group.groupName));

// --- Per-incident resource A/B labelling --------------------------------------
// Each distinct resource in the incident (usually 1–2: a redundant A/B pair)
// gets a stable letter + colour so the user can tell them apart at a glance.
// A/B colours follow the input/output scheme (INPUT = blue, OUTPUT = green) so
// the labels read consistently with the rest of the analytics UI. Two shades
// per family keep the redundant pair (51A / 51B) distinguishable; the first
// shade matches resourceTypeMeta's INPUT/OUTPUT text colour exactly.
const TYPE_PALETTE = {
  INPUT: [
    { dot: 'bg-blue-300', text: 'text-blue-300', badge: 'bg-blue-500/25 text-blue-200' },
    { dot: 'bg-blue-500', text: 'text-blue-400', badge: 'bg-blue-700/40 text-blue-300' },
  ],
  OUTPUT: [
    { dot: 'bg-green-400', text: 'text-green-400', badge: 'bg-green-500/25 text-green-200' },
    { dot: 'bg-emerald-600', text: 'text-emerald-400', badge: 'bg-emerald-700/40 text-emerald-300' },
  ],
};
// Fallback for resources with no/other type so they still get a stable colour.
const FALLBACK_PALETTE = [
  { dot: 'bg-violet-400', text: 'text-violet-300', badge: 'bg-violet-500/25 text-violet-200' },
  { dot: 'bg-teal-400', text: 'text-teal-300', badge: 'bg-teal-500/25 text-teal-200' },
  { dot: 'bg-pink-400', text: 'text-pink-300', badge: 'bg-pink-500/25 text-pink-200' },
  { dot: 'bg-orange-400', text: 'text-orange-300', badge: 'bg-orange-500/25 text-orange-200' },
];
const RESOURCE_LETTERS = 'ABCDEFGHIJKL';
const resourceKey = (a) => (a && (a.resourceId || a.resourceName)) || '';
const resourceLabels = computed(() => {
  const seen = new Map();
  sortedAnomalies.value.forEach((a) => {
    const key = resourceKey(a);
    if (!key || seen.has(key)) return;
    seen.set(key, { key, name: a.resourceName || a.resourceId || key, type: a.resourceType });
  });
  // Sort by name so a resource keeps the same letter (e.g. …-51A = A, …-51B = B).
  const ordered = Array.from(seen.values()).sort((x, y) => x.name.localeCompare(y.name));
  const map = new Map();
  // Shade index per type, so the first INPUT gets the light blue, the second the
  // darker blue, etc. — independent of the global A/B/C letter order.
  const perType = {};
  ordered.forEach((entry, i) => {
    const typeKey = (entry.type || '').toString().toUpperCase();
    const palette = TYPE_PALETTE[typeKey];
    let colour;
    if (palette) {
      const idx = perType[typeKey] || 0;
      colour = palette[idx % palette.length];
      perType[typeKey] = idx + 1;
    } else {
      colour = FALLBACK_PALETTE[i % FALLBACK_PALETTE.length];
    }
    map.set(entry.key, {
      key: entry.key,
      letter: RESOURCE_LETTERS[i] || String(i + 1),
      name: entry.name,
      type: entry.type,
      ...colour,
    });
  });
  return map;
});
// Only label when there's more than one resource to disambiguate.
const multiResource = computed(() => resourceLabels.value.size > 1);
const resourceLegend = computed(() => Array.from(resourceLabels.value.values()));
function resourceLabelFor(anomaly) {
  if (!multiResource.value) return null;
  return resourceLabels.value.get(resourceKey(anomaly)) || null;
}
// Prefer the real backend incident id (nested structure exposes it as
// `incidentId`); legacy anomalies use their own id as the incident id.
const groupIncidentIds = computed(() => [...new Set(sortedAnomalies.value.map((a) => a.incidentId || a.id))]);
const allAnalyzed = computed(() => sortedAnomalies.value.length > 0 && sortedAnomalies.value.every((a) => a.hasAi));

// Opens on the most severe anomaly rather than the earliest one.
function autoSelect() {
  if (!selectedAnomalyId.value && listAnomalies.value.length) {
    selectedAnomalyId.value = listAnomalies.value[0].id;
  }
}
// Prefer the explicitly requested anomaly (a clicked resource issue); fall
// back to the first anomaly in the group.
function applyInitialSelection() {
  if (props.initialAnomalyId && sortedAnomalies.value.some((a) => a.id === props.initialAnomalyId)) {
    selectedAnomalyId.value = props.initialAnomalyId;
    return;
  }
  autoSelect();
}
function selectAnomaly(anomaly) {
  selectedAnomalyId.value = anomaly.id;
}
function resourceMeta(type) {
  return resourceTypeMeta(type);
}
// Hover text for an anomaly's resource: name (type) + id.
function resourceTooltip(anomaly) {
  const name = anomaly.resourceName || anomaly.resourceId || '';
  const type = resourceMeta(anomaly.resourceType).label;
  const head = [name, type && type !== '-' ? `(${type})` : ''].filter(Boolean).join(' ');
  return anomaly.resourceId ? `${head}\nid: ${anomaly.resourceId}` : head;
}
// Hover text for a legend entry: name + id (key is the resourceId when present).
function legendTooltip(r) {
  return r.key && r.key !== r.name ? `${r.name}\nid: ${r.key}` : r.name;
}
function anomalyClass(anomaly) {
  const base = severityBg(anomaly.severity);
  // z-40 keeps the selected bar above the severity stacking (z-10..z-30).
  if (selectedAnomalyId.value === anomaly.id) return `${base} ring-2 ring-inset ring-white z-40`;
  return base;
}
function formatTime(value) {
  if (!value) return '--';
  return moment.utc(value).format('DD/MM HH:mm:ss');
}
function formatTimeSeconds(value) {
  if (!value) return '--';
  return moment.utc(value).format('HH:mm:ss');
}
function anomalyTooltip(anomaly) {
  const group = anomaly.groupId ? ` | ${anomaly.groupId}` : '';
  return `${(anomaly.severity || '').toUpperCase()} | ${anomaly.metricName}${group} | ${formatTime(anomaly.startMs)}`;
}

watch(() => props.group, () => {
  selectedAnomalyId.value = null;
  applyInitialSelection();
});
watch(() => props.initialAnomalyId, applyInitialSelection);

onMounted(() => {
  applyInitialSelection();
});
</script>
